<template>
  <footer class="site-footer">
    <div class="container">
      <div class="site-footer__grid">
        <section class="site-footer__brand">
          <component
            :is="logoLinkComponent"
            v-bind="logoLinkProps"
            class="site-footer__logo"
            aria-label="صفحه اصلی"
          >
            <img v-if="logo.src" :src="logo.src" :alt="logo.alt || ''" />
            <span v-else>{{ globalContent.companyName || 'همراه زر' }}</span>
          </component>

          <p class="site-footer__description">{{ config.description }}</p>

          <div class="site-footer__contact-list">
            <p v-if="contact.address">{{ contact.address }}</p>
            <p v-if="contact.workingHours">{{ contact.workingHours }}</p>
            <p v-for="phone in contact.phones || []" :key="phone">
              <a :href="`tel:${phone}`">{{ phone }}</a>
            </p>
            <p v-for="email in contact.emails || []" :key="email">
              <a :href="`mailto:${email}`">{{ email }}</a>
            </p>
          </div>
        </section>

        <section
          v-for="column in config.columns || []"
          :key="column._id || column.title"
          class="site-footer__column"
        >
          <h6 class="site-footer__title">{{ column.title }}</h6>
          <ul class="site-footer__links">
            <li
              v-for="link in column.links || []"
              :key="link._id || link.href"
              v-show="link.visible !== false"
            >
              <component
                :is="linkComponent(link.href)"
                v-bind="linkProps(link.href)"
                class="site-footer__link"
              >
                {{ link.label }}
              </component>
            </li>
          </ul>
        </section>

        <section class="site-footer__column">
          <h6 class="site-footer__title">لینک‌های مهم</h6>
          <div class="site-footer__chips">
            <component
              v-for="link in config.legalLinks || []"
              :is="linkComponent(link.href)"
              :key="`legal-${link._id || link.href}`"
              v-show="link.visible !== false"
              v-bind="linkProps(link.href)"
              class="site-footer__chip"
            >
              {{ link.label }}
            </component>

            <component
              v-for="link in config.appLinks || []"
              :is="linkComponent(link.href)"
              :key="`app-${link._id || link.href}`"
              v-show="link.visible !== false"
              v-bind="linkProps(link.href)"
              class="site-footer__chip"
            >
              {{ link.label }}
            </component>

            <component
              v-for="link in config.socialLinks || []"
              :is="linkComponent(link.href)"
              :key="`social-${link._id || link.href}`"
              v-show="link.visible !== false"
              v-bind="linkProps(link.href)"
              class="site-footer__chip"
            >
              {{ link.label }}
            </component>
          </div>
        </section>
      </div>

      <div class="site-footer__bottom">
        <small>{{ config.copyright || globalContent.copyright }}</small>
      </div>
    </div>
  </footer>
</template>

<script>
import {seoBrand} from "@/services/seoBrand";
import { getSiteCms, loadSiteCms } from '@/services/siteCms';

export default {
  data: () => ({ cms: getSiteCms() }),
  computed: {
    config() { return this.cms.footer || {}; },
    logo() { return seoBrand.logo ? {...(this.config.logo||{}),src:seoBrand.logo,alt:seoBrand.siteName} : this.config.logo || {}; },
    contact() { return this.config.contact || {}; },
    globalContent() { return this.cms.globalContent || {}; },
    logoLinkComponent() { return this.linkComponent(this.logo.href); },
    logoLinkProps() { return this.linkProps(this.logo.href); },
  },
  async created() {
    this.cms = await loadSiteCms();
    window.addEventListener('site-cms:loaded', this.updateCms);
  },
  unmounted() {
    window.removeEventListener('site-cms:loaded', this.updateCms);
  },
  methods: {
    updateCms(event) { this.cms = event.detail || {}; },
    isInternalLink(href) {
      return typeof href === 'string' && href.startsWith('/') && !href.startsWith('//');
    },
    linkComponent(href) {
      return this.isInternalLink(href) ? 'router-link' : 'a';
    },
    linkProps(href) {
      return this.isInternalLink(href)
        ? { to: href || '/' }
        : { href: href || '#', target: '_self' };
    },
  },
};
</script>

<style scoped>
.site-footer {
  direction: rtl;
  padding: 4rem 0 2rem;
  color: rgba(255, 255, 255, 0.88);
  background:
    radial-gradient(circle at top right, rgba(212, 175, 55, 0.12), transparent 28%),
    linear-gradient(180deg, #0d1018, #10131d 55%, #0c0f16);
  border-top: 1px solid rgba(212, 175, 55, 0.1);
}

.site-footer__grid {
  display: grid;
  grid-template-columns: minmax(0, 1.5fr) repeat(3, minmax(0, 1fr));
  gap: 2rem;
  align-items: start;
  text-align: right;
}

.site-footer__brand,
.site-footer__column {
  min-width: 0;
}

.site-footer__logo {
  display: inline-flex;
  align-items: center;
  min-height: 52px;
  color: #fff;
  text-decoration: none;
  font-weight: 800;
}

.site-footer__logo img {
  max-height: 50px;
  max-width: 170px;
  object-fit: contain;
}

.site-footer__description {
  margin-top: 1rem;
  margin-bottom: 0;
  line-height: 1.9;
  color: rgba(255, 255, 255, 0.76);
}

.site-footer__contact-list {
  display: grid;
  gap: 0.75rem;
  margin-top: 1.25rem;
}

.site-footer__contact-list p {
  margin: 0;
  line-height: 1.9;
}

.site-footer__contact-list a,
.site-footer__link,
.site-footer__chip {
  color: rgba(255, 255, 255, 0.9);
  text-decoration: none;
}

.site-footer__title {
  margin-bottom: 1rem;
  color: #fff;
  font-size: 1rem;
  font-weight: 800;
}

.site-footer__links {
  display: grid;
  gap: 0.75rem;
  margin: 0;
  padding: 0;
  list-style: none;
}

.site-footer__link {
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  line-height: 1.9;
  color: rgba(255, 255, 255, 0.76);
  transition: color 0.2s ease, transform 0.2s ease;
}

.site-footer__link:hover,
.site-footer__contact-list a:hover {
  color: #f0d77a;
  transform: translateX(-2px);
}

.site-footer__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.65rem;
  justify-content: flex-start;
}

.site-footer__chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 40px;
  padding: 0.55rem 0.9rem;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 999px;
  color: rgba(255, 255, 255, 0.82);
  background: rgba(255, 255, 255, 0.04);
  transition: all 0.2s ease;
}

.site-footer__chip:hover {
  color: #101010;
  background: #d4af37;
  border-color: #d4af37;
}

.site-footer__bottom {
  display: flex;
  justify-content: flex-start;
  margin-top: 2.25rem;
  padding-top: 1.25rem;
  border-top: 1px solid rgba(255, 255, 255, 0.12);
  color: rgba(255, 255, 255, 0.72);
  text-align: right;
}

@media (max-width: 991.98px) {
  .site-footer {
    padding: 3rem 0 1.75rem;
  }

  .site-footer__grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 1.5rem;
  }
}

@media (max-width: 575.98px) {
  .site-footer__grid {
    grid-template-columns: minmax(0, 1fr);
  }

  .site-footer__bottom {
    justify-content: center;
    text-align: center;
  }
}
</style>
